import React, { useEffect, useState } from "react";
import styles from "./submit-request-confirmation-step.module.scss";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { DynamicFieldItem } from "../../../../../model/dynamic-forms";
import { ServiceRequestType } from "../../../../../model/service-request/service-request";
import { Money } from "../../../../../model/common/money";
import { useGetPaymentFeeMutation } from "../../../../../service/charge-service";
import { formatDynamicFieldValue } from "../../../components/dynamic-form";

interface SubmitRequestConfirmationStepProps {
	serviceRequest: ServiceRequestType;
	sectionValues: DynamicFieldItem[][];
}

const NOT_CHARGED_DISCLAIMER =
	"You will not be charged at this stage. After submission, our team will review your request and contact you with the next steps.";

const SubmitRequestConfirmationStep: React.FC<SubmitRequestConfirmationStepProps> = ({ serviceRequest, sectionValues }) => {
	// Price = the request's fixed price, or the first entered field that carries a price (mirror getPriceFromChildrenOptions).
	const price = serviceRequest.price ?? sectionValues.flat().find(item => item.price)?.price;

	const [getPaymentFee] = useGetPaymentFeeMutation();
	const [fee, setFee] = useState<number | null>(null);

	useEffect(() => {
		if (!price || !price.currency || price.amount <= 0) {
			setFee(null);
			return;
		}
		let active = true;
		getPaymentFee({ currency: price.currency, amount: price.amount })
			.unwrap()
			.then(res => {
				if (active) setFee(res?.estimationFees?.[0]?.paymentChannels?.[0]?.clientFee?.amount ?? null);
			})
			.catch(() => {
				if (active) setFee(null);
			});
		return () => {
			active = false;
		};
	}, [price, getPaymentFee]);

	const formatMoney = (amount: number): string => new Money(price!.currency, amount).toLocaleString();

	return (
		<div className={styles.confirmation}>
			{price && (
				<CardFrame>
					<div className={styles.cardInner}>
						<div className={styles.titleRow}>
							<span className={styles.title}>Payment Summary</span>
						</div>
						<div className={styles.divider} />
						<div className={styles.body}>
							<div className={styles.titleRow}>
								<span className={styles.title}>Amount</span>
								<span className={styles.amountValue}>{formatMoney(price.amount)}</span>
							</div>
							{fee != null && (
								<div className={`${styles.titleRow} ${styles.feeRow}`}>
									<span className={styles.title}>Fee</span>
									<span className={styles.amountValue}>{formatMoney(fee)}</span>
								</div>
							)}
							{serviceRequest.payOnSubmit !== true && <p className={styles.disclaimer}>{NOT_CHARGED_DISCLAIMER}</p>}
						</div>
					</div>
				</CardFrame>
			)}

			{sectionValues.map((values, sectionIndex) => (
				<CardFrame key={serviceRequest.sectionDefinitions[sectionIndex]?.id ?? sectionIndex}>
					<div className={styles.cardInner}>
						<div className={styles.titleRow}>
							<span className={styles.title}>{serviceRequest.sectionDefinitions[sectionIndex]?.name ?? ""}</span>
						</div>
						<div className={styles.divider} />
						<div className={styles.body}>
							{values.length > 0 ? (
								values.map(item => (
									<div key={item.fieldId} className={styles.fieldRow}>
										<span className={styles.fieldLabel}>{item.fieldLabel}</span>
										<span className={styles.fieldValue}>{formatDynamicFieldValue(item)}</span>
									</div>
								))
							) : (
								<p className={styles.emptyText}>No data is entered in this section</p>
							)}
						</div>
					</div>
				</CardFrame>
			))}
		</div>
	);
};

export default SubmitRequestConfirmationStep;
